// Credits & activity guide — the credit counter next to the user name in the
// top navbar (refresh + ⓘ popover) and the ⋮ → Activity History ledger
// (EditorNavBar, ActivityLogModal). Locale-keyed like every guide
// (content.<lang> → { feature, title, sections }).

const creditsActivityGuide = {
  id: "credits-activity",
  icon: "account_balance_wallet",
  keywords: [
    "credit",
    "credits",
    "kredi",
    "krediler",
    "balance",
    "bakiye",
    "remaining credit",
    "kalan kredi",
    "account",
    "hesap",
    "organization",
    "kuruluş",
    "organizasyon",
    "team",
    "ekip",
    "takım",
    "budget",
    "bütçe",
    "team budget",
    "takım bütçesi",
    "activity",
    "aktivite",
    "activity history",
    "aktivite geçmişi",
    "transactions",
    "işlemler",
    "spent",
    "harcanan",
    "cost",
    "maliyet",
    "insufficient balance",
    "yetersiz bakiye",
    "top up",
    "kredi yükle",
    "crédito",
    "créditos",
    "saldo",
    "crédito restante",
    "cuenta",
    "organización",
    "equipo",
    "presupuesto",
    "historial de actividad",
    "transacciones",
    "gastado",
    "saldo insuficiente",
  ],
  content: {
    en: {
      feature: "Account",
      title: "Credits & Activity History",
      sections: [
        {
          id: "credit-counter",
          title: "The credit counter",
          videoUrl: null,
          text:
            "Your remaining credits are shown in the top bar, right next to your name, with a lightning icon and the word **credits**. Large balances are shortened (for example **1.2K**); hover over the number to see the exact figure.\n\n" +
            "- The counter refreshes itself about once a minute, so it goes down while an AI job is running.\n" +
            "- Click the small refresh arrow next to the number to update it right away.\n" +
            "- Click the **ⓘ** icon to see what the number means (see below).",
        },
        {
          id: "what-the-number-means",
          title: "What the number means",
          videoUrl: null,
          text:
            "The **ⓘ** popover (**Remaining credit**) explains which limit the number comes from. It is never what this course alone has cost.\n\n" +
            "- **Organization balance** — by default the number is your organization's total remaining credit, the whole account's available balance.\n" +
            "- **Your share in a team** — if your organization gave you a personal monthly share in a team, the number is what is left of your own share.\n" +
            "- **Team pool** — if your team shares one monthly budget, the number is what the team can still spend, shared with the other people in the team.\n\n" +
            "When a team budget applies, the team's name is shown next to the number and the popover tells you the date the limit resets.",
        },
        {
          id: "activity-history",
          title: "Activity History",
          videoUrl: null,
          text:
            "Open the **⋮** menu in the top bar and choose **Activity History** to see a breakdown of the credits spent on the course you have open.\n\n" +
            "- At the top, **Total spent** and **Transactions** summarize the whole course, not just the page you are looking at.\n" +
            "- Each row shows the **Date**, the **Operation** (what was produced, with the job's status under it), the **User** who started it, and the **Credits** — refunds and top-ups are shown with **+** in green, spending with **−** in red.\n" +
            "- The list shows 15 rows per page; use the arrows at the bottom to page through.\n" +
            "- The refresh icon reloads the list. If loading fails, click **Try again**.",
        },
        {
          id: "spending-credits",
          title: "What uses credits",
          videoUrl: null,
          text:
            "Credits are spent by the AI features — for example generating or editing images, AI voices and narration, avatars, video generation, the Quiz Generator and the Aidea Assistant's generation actions.\n\n" +
            "- Some AI windows and Assistant cards show the expected cost before you confirm.\n" +
            "- If there aren't enough credits, an **Insufficient Balance** window appears; use **Contact Us** to send a request for more credits.\n" +
            "- If your team's monthly budget is used up, you see **Your team budget is used up** instead, with the date it renews — ask your organization admin if you need more before then.",
        },
      ],
    },
    tr: {
      feature: "Hesap",
      title: "Krediler ve Aktivite Geçmişi",
      sections: [
        {
          id: "credit-counter",
          title: "Kredi sayacı",
          videoUrl: null,
          text:
            "Kalan krediniz, üst çubukta adınızın hemen yanında şimşek simgesi ve **kredi** sözcüğüyle gösterilir. Büyük bakiyeler kısaltılır (örneğin **1.2K**); tam değeri görmek için farenizi sayının üzerine getirin.\n\n" +
            "- Sayaç yaklaşık dakikada bir kendini yeniler; böylece bir yapay zeka işi çalışırken düştüğünü görürsünüz.\n" +
            "- Hemen güncellemek için sayının yanındaki küçük yenile okuna (**Yenile**) tıklayın.\n" +
            "- Sayının ne anlama geldiğini görmek için **ⓘ** simgesine tıklayın (aşağıya bakın).",
        },
        {
          id: "what-the-number-means",
          title: "Sayı neyi gösterir",
          videoUrl: null,
          text:
            "**ⓘ** açılır penceresi (**Kalan kredi**), sayının hangi limitten geldiğini açıklar. Bu sayı hiçbir zaman yalnızca bu eğitimin maliyeti değildir.\n\n" +
            "- **Kuruluş bakiyesi** — varsayılan olarak sayı, kuruluşunuzun kalan toplam kredisidir; yani tüm hesabın kullanılabilir bakiyesi.\n" +
            "- **Ekipteki payınız** — kuruluşunuz size bir ekip içinde aylık kişisel pay ayırdıysa, sayı kendi payınızdan kalan kredidir.\n" +
            "- **Ekip havuzu** — ekibiniz tek bir aylık bütçeyi paylaşıyorsa, sayı ekibin bu ay hâlâ harcayabileceği, ekipteki diğer kişilerle ortak kullanılan kredidir.\n\n" +
            "Bir ekip bütçesi geçerliyse sayının yanında ekibin adı görünür ve açılır pencere limitin yenileneceği tarihi belirtir.",
        },
        {
          id: "activity-history",
          title: "Aktivite Geçmişi",
          videoUrl: null,
          text:
            "Üst çubuktaki **⋮** menüsünü açıp **Aktivite Geçmişi**'ni seçin; açık olan eğitim için harcanan kredilerin dökümünü görürsünüz.\n\n" +
            "- Üstteki **Toplam harcanan** ve **İşlem sayısı**, yalnızca baktığınız sayfayı değil tüm eğitimi özetler.\n" +
            "- Her satırda **Tarih**, **İşlem** (neyin üretildiği ve altında işin durumu), işlemi başlatan **Kullanıcı** ve **Kredi** yer alır — iadeler ve yüklemeler yeşil **+**, harcamalar kırmızı **−** ile gösterilir.\n" +
            "- Liste sayfa başına 15 satır gösterir; sayfalar arasında gezinmek için alttaki okları kullanın.\n" +
            "- Yenile simgesi listeyi yeniden yükler. Yükleme başarısız olursa **Tekrar dene**'ye tıklayın.",
        },
        {
          id: "spending-credits",
          title: "Krediyi neler harcar",
          videoUrl: null,
          text:
            "Krediler yapay zeka özellikleri tarafından harcanır — örneğin görsel oluşturma veya düzenleme, yapay zeka sesleri ve seslendirme, avatarlar, video oluşturma, Soru Üretici ve Aidea Asistan'ın üretim işlemleri.\n\n" +
            "- Bazı yapay zeka pencereleri ve asistan kartları, onaylamadan önce beklenen maliyeti gösterir.\n" +
            "- Yeterli kredi yoksa **Yetersiz Bakiye** penceresi açılır; daha fazla kredi talep etmek için **Bizimle İletişime Geçin**'i kullanın.\n" +
            "- Ekibinizin aylık bütçesi dolduysa bunun yerine bütçenin yenileneceği tarihle birlikte **Takım bütçeniz doldu** mesajını görürsünüz — o tarihten önce ihtiyacınız varsa organizasyon yöneticinizle görüşün.",
        },
      ],
    },
    es: {
      feature: "Cuenta",
      title: "Créditos e Historial de actividad",
      sections: [
        {
          id: "credit-counter",
          title: "El contador de créditos",
          videoUrl: null,
          text:
            "Tus créditos restantes se muestran en la barra superior, justo al lado de tu nombre, con un icono de rayo y la palabra **créditos**. Los saldos grandes se abrevian (por ejemplo, **1.2K**); pasa el ratón por encima del número para ver la cifra exacta.\n\n" +
            "- El contador se actualiza solo aproximadamente una vez por minuto, así que va bajando mientras se ejecuta un trabajo de IA.\n" +
            "- Haz clic en la pequeña flecha de actualizar junto al número para actualizarlo al momento.\n" +
            "- Haz clic en el icono **ⓘ** para ver qué significa el número (consulta más abajo).",
        },
        {
          id: "what-the-number-means",
          title: "Qué significa el número",
          videoUrl: null,
          text:
            "La ventana emergente **ⓘ** (**Crédito restante**) explica de qué límite procede el número. Nunca es lo que ha costado solo este curso.\n\n" +
            "- **Saldo de la organización**: de forma predeterminada, el número es el crédito total restante de tu organización, es decir, el saldo disponible de toda la cuenta.\n" +
            "- **Tu asignación en un equipo**: si tu organización te asignó una cuota mensual personal dentro de un equipo, el número es lo que queda de tu propia asignación.\n" +
            "- **Fondo común del equipo**: si tu equipo comparte un único presupuesto mensual, el número es lo que el equipo aún puede gastar, compartido con las demás personas del equipo.\n\n" +
            "Cuando se aplica un presupuesto de equipo, el nombre del equipo aparece junto al número y la ventana emergente indica la fecha en que se renueva el límite.",
        },
        {
          id: "activity-history",
          title: "Historial de actividad",
          videoUrl: null,
          text:
            "Abre el menú **⋮** de la barra superior y elige **Historial de actividad** para ver el desglose de los créditos gastados en el curso que tienes abierto.\n\n" +
            "- Arriba, **Total gastado** y **Transacciones** resumen todo el curso, no solo la página que estás viendo.\n" +
            "- Cada fila muestra la **Fecha**, la **Operación** (lo que se produjo, con el estado del trabajo debajo), el **Usuario** que la inició y los **Créditos**: los reembolsos y las recargas se muestran con **+** en verde, y los gastos con **−** en rojo.\n" +
            "- La lista muestra 15 filas por página; usa las flechas de la parte inferior para pasar de página.\n" +
            "- El icono de actualizar recarga la lista. Si la carga falla, haz clic en **Intentar de nuevo**.",
        },
        {
          id: "spending-credits",
          title: "Qué consume créditos",
          videoUrl: null,
          text:
            "Los créditos se consumen con las funciones de IA: por ejemplo, generar o editar imágenes, las voces y la narración con IA, los avatares, la generación de vídeo, el Generador de Preguntas y las acciones de generación del Asistente Aidea.\n\n" +
            "- Algunas ventanas de IA y tarjetas del Asistente muestran el costo previsto antes de que confirmes.\n" +
            "- Si no hay créditos suficientes, aparece la ventana **Saldo Insuficiente**; usa **Contáctanos** para enviar una solicitud de más créditos.\n" +
            "- Si se ha agotado el presupuesto mensual de tu equipo, verás en su lugar **El presupuesto de tu equipo se ha agotado**, con la fecha en que se renueva; si necesitas más antes de esa fecha, habla con el administrador de tu organización.",
        },
      ],
    },
  },
};

export default creditsActivityGuide;
